import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Leg } from '@approot/models';
import { pairwise } from 'rxjs';
import { clearCustomText, clearTextColor } from '../utilities/leg.utilities';
import { updateTotalNotional } from '../utilities/notional-utilities';
import { LegHelperService } from './leg-helper.service';
import { LegScheduleService } from './leg-schedule.service';
@Injectable({
  providedIn: 'root'
})
export class LegScheduleFormListenersService {
  constructor(
    private legHelperService: LegHelperService,
    private legScheduleService: LegScheduleService
  ) {}

  public scheduleListeners(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    this.nbListeners(form);
    this.frequencyListener(form, leg);
    this.exerciseFrequencyListener(form);
    this.fixFloatListener(form);
    this.fixHolidayCalendarsListener(form);
    this.conventionListeners(form);
  }

  private nbListeners(form: FormGroup): void {
    ['nbFixings', 'nbSettlementDates', 'nbExpiries'].forEach(nbDate => {
      this.legHelperService.subSink.sink = form
        .get(nbDate)
        ?.valueChanges.subscribe(_ => {
          updateTotalNotional(
            form,
            this.legHelperService.leg,
            this.legHelperService.presetsLoaded
          );
          clearCustomText(form); // if the nb of fixings changes (which is crucial to the schedule)
          // then wipe all customs from the leg, as we don't know how to adapt these already set Custom's to the new schedule
        });
    });
  }

  private frequencyListener(form: FormGroup, leg: Leg): void {
    this.legHelperService.subSink.sink = form
      .get('frequency')
      ?.valueChanges.subscribe(_ => {
        clearTextColor(leg, 'frequency');
        clearCustomText(form, ['frequency']); // if the frequency of fixings changes (which is crucial to the schedule)
        // then wipe all customs from the leg, as we don't know how to adapt these already set Custom's to the new schedule
        this.legScheduleService.callScheduleGenerator();
      });

    this.legHelperService.subSink.sink = form
      .get('frequency1')
      ?.valueChanges.subscribe(frequency1 => {
        this.legScheduleService.xccySwapGetSchedules({
          frequency1
        });
      });

    this.legHelperService.subSink.sink = form
      .get('frequency2')
      ?.valueChanges.subscribe(frequency2 => {
        this.legScheduleService.xccySwapGetSchedules({ frequency2 });
      });

    this.legHelperService.subSink.sink = form
      .get('settlementFrequency')
      ?.valueChanges.subscribe(_ => {
        this.legScheduleService.callScheduleGenerator();
      });
  }

  private conventionListeners(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('xccySwapFixedLegConvention1')
      ?.valueChanges.pipe(pairwise())
      .subscribe(([prevValue, value]) => {
        if (!prevValue) {
          this.legScheduleService.xccySwapGetSchedules({
            xccySwapFixedLegConvention1: value
          });
        }
      });

    this.legHelperService.subSink.sink = form
      .get('xccySwapFixedLegConvention2')
      ?.valueChanges.pipe(pairwise())
      .subscribe(([prevValue, value]) => {
        if (!prevValue) {
          this.legScheduleService.xccySwapGetSchedules({
            xccySwapFixedLegConvention2: value
          });
        }
      });
  }

  private exerciseFrequencyListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('exerciseFrequency')
      ?.valueChanges.subscribe(() => {
        this.legScheduleService.exerciseScheduleGenerator();
      });
  }

  private fixFloatListener(form: FormGroup): void {
    [
      'fixedLegFrequency',
      'fixedLegConvention',
      'floatingLegFrequency',
      'floatingLegConvention'
    ].forEach(field => {
      this.legHelperService.subSink.sink = form
        .get(field)
        ?.valueChanges.subscribe(() => {
          this.legScheduleService.callSwapScheduleGenerator(
            field === 'fixedLegFrequency' || field === 'fixedLegConvention'
              ? 'fixedLegSchedule'
              : 'floatingLegSchedule'
          );
        });
    });
  }

  private fixHolidayCalendarsListener(form: FormGroup): void {
    if (form.get('fixingHolidayCalendars')) {
      this.legHelperService.subSink.sink = form
        .get('fixingHolidayCalendars')
        .valueChanges.subscribe(x => {
          clearCustomText(form);
          this.legScheduleService.callScheduleGenerator();
        });
    }
  }
}
